import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {useGetSessionContext} from "../context/get-session";
import {OfferingFormCheckoutFormValues} from "../../../../components/offering-form/general/checkout-form/types";
import {offeringCheckoutFormValidationSchema} from "../../../../components/offering-form/general/checkout-form/validation-schema";
import {useEditService} from "../hooks/edit-service";
import {SessionCheckoutFormFields} from "../../../../components/offering-form/session-bundle/session/checkout-form";
import {useNavigate} from "react-router-dom";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {EditSessionFormValues} from "../types";
import {ServiceFormLayout} from "../../createService/service-form-layout";
import {useDeleteSessionHook} from "../hooks/delete-service";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../features/media";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {toSavedEditSessionFormValues} from "../saved-form-values";

export function EditSessionCheckoutForm() {
  const navigate = useNavigate();
  const {
    state: {sessionData},
  } = useGetSessionContext();
  const {deleteSession} = useDeleteSessionHook(sessionData.id);

  // Fetch cover images via v2 with fallback to embedded data
  const {coverImages} = useCoverImagesWithFallback({
    offeringType: "service",
    offeringId: sessionData.id,
    embedded: sessionData.cover_images,
  });

  // Convert domain format to API format for payload
  const coverImagesForPayload = mapDomainToEmbedded(coverImages);
  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(offeringCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      bookingQuestions: sessionData.bookingQuestions
        ? sessionData.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
              };
            }
          })
        : [],
    },
  });
  const {editSession, isEditingSession} = useEditService();
  function onSubmit(values: OfferingFormCheckoutFormValues) {
    // The update carries the whole session, so everything this tab does not edit goes
    // back exactly as saved: pricing model, currencies, redirect, location and schedule.
    const payload: EditSessionFormValues = {
      ...toSavedEditSessionFormValues(sessionData, IS_PWYW_ENABLED),
      id: sessionData.id,
      cover_images: coverImagesForPayload,
      bookingQuestions: values.bookingQuestions,
    };
    editSession(payload);
  }
  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;
  return (
    <ServiceFormLayout>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <SessionCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => navigate("/calls")}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1
                    }
                    loading={isEditingSession}
                  >
                    Update session
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this session? This action cannot be undone."
                    okText="Delete session"
                    onOk={deleteSession}
                    title="Confirm session deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}
